import React, { useState } from "react";
import {
  signInWithEmailAndPassword,
  sendEmailVerification,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
} from "firebase/auth"; // Import the signIn function
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { FcGoogle as FcGoogleIcon } from "react-icons/fc";
import { FaApple as FaAppleIcon } from "react-icons/fa";
import { Capacitor } from "@capacitor/core";
import { doc, getDocFromServer, setDoc } from "firebase/firestore";
import { auth, db } from "../Configuration/firebase"; // Import Firebase authentication
import { useNavigate, Link } from "react-router-dom"; // Import useNavigate for routing
import { friendlyAuthError } from "../utils/authErrors";

const upsertUserProfile = async (user) => {
  // Apple only returns the user's name on the very first sign-in (and Google
  // users may have an empty displayName too). Only write the name fields when
  // we actually have a name so a later sign-in with a blank displayName can't
  // wipe out a name we already stored.
  const profile = {
    email: user.email,
  };
  const displayName = (user.displayName || "").trim();
  if (displayName) {
    const nameParts = displayName.split(" ");
    profile.firstName = nameParts[0] || "";
    profile.lastName = nameParts.slice(1).join(" ") || "";
  }
  const userRef = doc(db, "users", user.uid);
  // Only seed default categories for a genuinely brand-new profile, and decide
  // that from an authoritative SERVER read. A plain getDoc right after sign-in
  // can silently return the (empty) local cache before the Firestore
  // connection is up - especially on iOS, where we force long-polling - and
  // report the doc as missing. Because merge:true replaces array fields
  // wholesale, seeding on that false negative overwrote real categories with
  // the defaults. getDocFromServer avoids the cache; if it can't reach the
  // server we skip seeding entirely rather than risk clobbering.
  try {
    const serverDoc = await getDocFromServer(userRef);
    if (!serverDoc.exists()) {
      profile.categories = ["Home", "Work"];
    }
  } catch (readError) {
    console.warn(
      "Skipping category seed; couldn't confirm profile from server:",
      readError,
    );
  }
  await setDoc(userRef, profile, { merge: true });
};

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const [resendStatus, setResendStatus] = useState("");
  const navigate = useNavigate();

  const signIn = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;
      if (user.emailVerified) {
        console.log("User signed in:", user); // Debugging: logs user data
        navigate("/"); // Redirect to home page after sign-in
      } else {
        setUnverifiedUser(user);
        setResendStatus("");
      }
    } catch (error) {
      setError(friendlyAuthError(error));
      console.error("Error during sign-in:", error); // Debugging: logs error message
    }
  };

  const resendVerificationEmail = async () => {
    if (!unverifiedUser) return;
    setResendStatus("Sending...");
    try {
      const idToken = await unverifiedUser.getIdToken();
      const response = await fetch(
        "https://us-central1-one-address-53a06.cloudfunctions.net/sendVerificationEmail",
        {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );
      if (!response.ok) {
        throw new Error(`sendVerificationEmail returned ${response.status}`);
      }
    } catch (resendError) {
      console.error(
        "Custom verification email failed, falling back to default:",
        resendError,
      );
      try {
        await sendEmailVerification(unverifiedUser);
      } catch (fallbackError) {
        console.error("Fallback verification email failed too:", fallbackError);
        setResendStatus(
          "Couldn't send the email right now. Please try again in a bit.",
        );
        return;
      }
    }
    setResendStatus(
      "Verification email sent - check your inbox (and spam folder).",
    );
  };

  const signInWithGoogle = async () => {
    setError("");
    setGoogleLoading(true);
    try {
      let user;
      if (Capacitor.isNativePlatform()) {
        // Native SDK handles the OAuth flow (avoids Google's WebView block),
        // then we bridge the resulting credential into this JS SDK's auth
        // instance so onAuthStateChanged/Firestore calls see the session too.
        // useCredentialManager: false - the new Android Credential Manager
        // flow (default since plugin 7.2.0) kept returning "no credentials
        // available" even after fixing the signing certificate and
        // serverClientId; falling back to the legacy Google Sign-In SDK
        // path instead.
        // skipNativeAuth: true - we complete the Firebase sign-in ourselves
        // below via signInWithCredential; without this the native plugin
        // also calls Auth.auth().signIn(with:), redeeming the credential
        // twice.
        const result = await FirebaseAuthentication.signInWithGoogle({
          useCredentialManager: false,
          skipNativeAuth: true,
        });
        const idToken = result.credential?.idToken;
        if (!idToken) {
          throw new Error("Google sign-in did not return an ID token.");
        }
        const credential = GoogleAuthProvider.credential(idToken);
        const userCredential = await signInWithCredential(auth, credential);
        user = userCredential.user;
      } else {
        const provider = new GoogleAuthProvider();
        const userCredential = await signInWithPopup(auth, provider);
        user = userCredential.user;
      }

      await upsertUserProfile(user);
      navigate("/");
    } catch (googleError) {
      setError(
        friendlyAuthError(
          googleError,
          "Couldn't sign in with Google. Please try again.",
        ),
      );
      console.error("Error during Google sign-in:", googleError);
    } finally {
      setGoogleLoading(false);
    }
  };

  const signInWithApple = async () => {
    setError("");
    setAppleLoading(true);
    try {
      let user;
      if (Capacitor.isNativePlatform()) {
        // Native SDK handles the Sign in with Apple flow (ASAuthorization),
        // then we bridge the resulting credential into this JS SDK's auth
        // instance so onAuthStateChanged/Firestore calls see the session too -
        // mirrors the native Google path above.
        // skipNativeAuth: true - without it the native plugin redeems the
        // idToken+nonce with Firebase itself, then signInWithCredential below
        // redeems the same pair again, which Firebase rejects as a replay
        // ("Duplicate credentials received", auth/missing-or-invalid-nonce).
        const result = await FirebaseAuthentication.signInWithApple({
          skipNativeAuth: true,
        });
        const idToken = result.credential?.idToken;
        if (!idToken) {
          throw new Error("Apple sign-in did not return an ID token.");
        }
        const provider = new OAuthProvider("apple.com");
        const credential = provider.credential({
          idToken,
          // rawNonce must match the nonce the plugin sent to Apple (it forwards
          // the SHA-256 hash); Firebase rejects the credential otherwise.
          rawNonce: result.credential?.nonce,
        });
        const userCredential = await signInWithCredential(auth, credential);
        user = userCredential.user;
        // Apple only returns the name on first sign-in and doesn't populate
        // Firebase's displayName. Build one from the name it did give us so the
        // profile upsert can store it.
        const givenName = result.user?.displayName;
        if (givenName && !user.displayName) {
          user = { ...user, displayName: givenName };
        }
      } else {
        const provider = new OAuthProvider("apple.com");
        provider.addScope("email");
        provider.addScope("name");
        const userCredential = await signInWithPopup(auth, provider);
        user = userCredential.user;
      }

      await upsertUserProfile(user);
      navigate("/");
    } catch (appleError) {
      setError(
        friendlyAuthError(
          appleError,
          "Couldn't sign in with Apple. Please try again.",
        ),
      );
      console.error("Error during Apple sign-in:", appleError);
    } finally {
      setAppleLoading(false);
    }
  };

  const navigateToSignUp = () => {
    navigate("/signup"); // Navigate to the sign-up page
  };

  return (
    <div
      className="container"
      style={{
        width: "80%",
        maxWidth: "400px",
        margin: "auto",
        paddingTop: "calc(20px + env(safe-area-inset-top, 0px))",
      }}
    >
      <h2>Sign In</h2>
      <button
        onClick={signInWithGoogle}
        disabled={googleLoading}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          width: "90%",
          padding: "10px",
          marginBottom: "10px",
          backgroundColor: "var(--card-bg, #fff)",
          border: "1px solid #dadce0",
          borderRadius: "5px",
          cursor: googleLoading ? "default" : "pointer",
          fontWeight: "500",
          color: "#3c4043",
        }}
      >
        <FcGoogleIcon />
        {googleLoading ? "Signing in..." : "Continue with Google"}
      </button>
      <button
        onClick={signInWithApple}
        disabled={appleLoading}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          width: "90%",
          padding: "10px",
          marginBottom: "10px",
          backgroundColor: "#000",
          border: "1px solid #000",
          borderRadius: "5px",
          cursor: appleLoading ? "default" : "pointer",
          fontWeight: "500",
          color: "#fff",
        }}
      >
        <FaAppleIcon />
        {appleLoading ? "Signing in..." : "Continue with Apple"}
      </button>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "90%",
          margin: "10px 0",
        }}
      >
        <div style={{ flex: 1, height: "1px", backgroundColor: "#ddd" }} />
        <span style={{ padding: "0 10px", color: "#666", fontSize: "13px" }}>
          or
        </span>
        <div style={{ flex: 1, height: "1px", backgroundColor: "#ddd" }} />
      </div>
      <input
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{ width: "90%", padding: "10px", marginBottom: "10px" }}
      />
      <input
        type="password"
        placeholder="Enter your password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        style={{ width: "90%", padding: "10px", marginBottom: "10px" }}
      />
      <button
        onClick={signIn}
        style={{
          width: "25%",
          padding: "10px",
          backgroundColor: "#4CAF50",
          color: "white",
          borderRadius: "5px",
          marginRight: "10px",
          border: "none",
        }}
      >
        Sign In
      </button>
      <Link to="/forgot-password">Forgot Password?</Link>
      {error && <p style={{ color: "red" }}>{error}</p>}{" "}
      {/* Display error if exists */}
      {unverifiedUser && (
        <div style={{ marginTop: "10px", textAlign: "center" }}>
          <p style={{ color: "#b45309" }}>
            Please verify your email before logging in.
          </p>
          <button
            onClick={resendVerificationEmail}
            style={{
              padding: "8px 16px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Resend verification email
          </button>
          {resendStatus && (
            <p style={{ fontSize: "13px", color: "#555" }}>{resendStatus}</p>
          )}
        </div>
      )}
      <div style={{ marginTop: "10px", textAlign: "left" }}>
        <span>Don't have an account?</span>
        <button
          onClick={navigateToSignUp}
          style={{
            marginLeft: "5px",
            color: "#007BFF",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          Sign Up
        </button>
      </div>
    </div>
  );
};

export default SignIn;
